/**
 * 프린터/브릿지 상태 헤더 인디케이터
 * docs/tools_all.md §4 (헤더 상태 표시)
 *
 * - USB 브릿지 연결 + 첫 USB 프린터 reachable 여부 60초 폴링
 * - 클릭 시 즉시 재조회
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip, Spin } from 'antd';
import { PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import { checkUsbBridge, getStorePrinters } from '../services/api';

const POLL_MS = 60_000;

export default function PrinterStatusBar() {
  const [state, setState] = useState({ loading: true, bridge: null, usbReady: false, count: 0 });
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const [bridge, list] = await Promise.all([
        checkUsbBridge().catch(() => ({ connected: false })),
        getStorePrinters().catch(() => ({ printers: [] })),
      ]);
      const printers = list.printers || [];
      const usbList = printers.filter((p) => p.connection_type === 'usb');
      const usbReady = bridge.connected && usbList.length > 0;
      setState({ loading: false, bridge: bridge.connected, usbReady, count: printers.length });
    } catch {
      setState({ loading: false, bridge: false, usbReady: false, count: 0 });
    }
  }, []);

  useEffect(() => {
    refresh();
    timerRef.current = setInterval(refresh, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [refresh]);

  const { loading, bridge, usbReady, count } = state;
  const color = !bridge ? '#C62828' : usbReady ? '#43A047' : '#FB8C00';
  const label = !bridge ? '브릿지 OFF' : usbReady ? `프린터 ${count}` : '프린터 미연결';
  const tip = !bridge
    ? 'USB 브릿지 앱 미연결 — bridge-app 실행 확인 필요'
    : usbReady
      ? `USB 프린터 정상 (${count}대)`
      : 'USB 프린터 미연결 — 케이블/전원 확인 필요';

  return (
    <Tooltip title={`${tip} · 클릭하여 새로고침`}>
      <span
        onClick={refresh}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 8px',
          borderRadius: 12,
          background: '#F5F7FA',
          color,
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          userSelect: 'none',
        }}
        data-testid="printer-status-bar"
      >
        {loading ? <Spin size="small" /> : <PrinterOutlined />}
        {label}
        <ReloadOutlined style={{ fontSize: 10, opacity: 0.5 }} />
      </span>
    </Tooltip>
  );
}
